import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import {
  AZURE_CLIENT_ID,
  AZURE_POST_LOGOUT_REDIRECT_URI,
  AZURE_REDIRECT_URI,
  AZURE_TENANT_ID,
  MICROSOFT_SSO_ENABLED,
} from '../config';

const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

let initialized = false;
let msalInstance: PublicClientApplication | null = null;
let redirectStateSynced = false;

const getCurrentOrigin = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin.replace(/\/$/, '');
};

const getRedirectUri = () => {
  const currentOrigin = getCurrentOrigin();
  if (currentOrigin) {
    // Evita redirecionar para outro ambiente quando o build foi promovido entre dev/prod.
    return `${currentOrigin}/auth/callback`;
  }
  return AZURE_REDIRECT_URI || '';
};

const getPostLogoutRedirectUri = () => {
  const currentOrigin = getCurrentOrigin();
  if (currentOrigin) {
    return `${currentOrigin}/logout/callback`;
  }
  return AZURE_POST_LOGOUT_REDIRECT_URI || '';
};

const hasBrowserCryptoSupport = () => {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : undefined;
  return Boolean(window.isSecureContext && cryptoObj && typeof cryptoObj.getRandomValues === 'function');
};

const getSsoUnavailableReason = () => {
  if (!MICROSOFT_SSO_ENABLED) {
    return 'SSO Microsoft nao esta configurado neste ambiente.';
  }
  if (!hasBrowserCryptoSupport()) {
    return 'SSO Microsoft requer HTTPS (ou localhost) com suporte a crypto no navegador.';
  }
  return null;
};

export const isMicrosoftSsoConfigured = () => MICROSOFT_SSO_ENABLED;

const getMsalInstance = () => {
  if (msalInstance) return msalInstance;

  msalInstance = new PublicClientApplication({
    auth: {
      clientId: AZURE_CLIENT_ID || 'disabled-client-id',
      authority: `https://login.microsoftonline.com/${AZURE_TENANT_ID || 'common'}`,
      redirectUri: getRedirectUri(),
      postLogoutRedirectUri: getPostLogoutRedirectUri(),
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  });

  return msalInstance;
};

const ensureInitialized = async () => {
  const reason = getSsoUnavailableReason();
  if (reason) throw new Error(reason);

  const instance = getMsalInstance();
  if (!initialized) {
    await instance.initialize();
    initialized = true;
  }

  return instance;
};

export const isMicrosoftSsoEnabled = () => !getSsoUnavailableReason();

export const beginMicrosoftLogin = async () => {
  const instance = await ensureInitialized();
  
  // Limpa estado de interação pendente para evitar erros de fluxo travado
  localStorage.removeItem('msal.interaction.status');

  await instance.loginRedirect(loginRequest);
};

export const handleMicrosoftRedirect = async (): Promise<AuthenticationResult | null> => {
  if (!isMicrosoftSsoEnabled()) return null;

  const instance = await ensureInitialized();
  
  // handleRedirectPromise deve ser processado apenas uma vez
  const redirectResult = await instance.handleRedirectPromise();

  if (redirectResult?.account) {
    instance.setActiveAccount(redirectResult.account);
    return redirectResult;
  }

  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  if (!account) return null;

  instance.setActiveAccount(account);
  try {
    return await instance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
  } catch (error) {
    return null;
  }
};

export const beginMicrosoftLogout = async () => {
  if (!isMicrosoftSsoEnabled()) return;
  const instance = await ensureInitialized();

  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  await instance.logoutRedirect({
    account,
    postLogoutRedirectUri: getPostLogoutRedirectUri(),
  });
};
